<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from 'vue-i18n'
import { useAnomalyDetectorStore } from '@/store/anomaly_detector'

const { t } = useI18n()
const store = useAnomalyDetectorStore()
const { hasAppliedSearch, tablePage, tableItemsPerPage } = storeToRefs(store)
const hasCompanies = computed(() => store.getAnomalyDetectorsData.some((a) => (a?.sensor?.companies?.length ?? 0) > 0))

const allColumns = ref([
	{ title: 'ID', key: 'id', sortable: false },
	{ title: 'Empresa', key: 'company', requiresCompanyData: true },
	{ title: 'Sensor', key: 'sensor_name', sortable: false },
	{ title: 'Tipo de sensor', key: 'sensor_type', sortable: false },
	{ title: 'Campo', key: 'data_field', sortable: false },
	{ title: 'Mediana do ano', key: 'base_median', sortable: false },
	{ title: 'IQR do ano', key: 'base_iqr', sortable: false },
	{ title: 'Mediana da semana', key: 'week_median', sortable: false },
	{ title: 'IQR da semana', key: 'week_iqr', sortable: false },
	{ title: 'Variabilidade Mediana', key: 'variation', sortable: false },
	{ title: 'Nível de variação', key: 'variation_level', sortable: false },
	{ title: 'Alarmes na semana', key: 'week_alarms', sortable: false },
	{ title: 'Mensagem', key: 'message', sortable: false },
])

const columns = computed(() =>
	allColumns.value.filter((column) => {
		const allowedByCompanyData = !column.requiresCompanyData || hasCompanies.value
		return allowedByCompanyData
	}))

const rows = computed(() => store.getAnomalyDetectorsData)
const pagination = computed(() => store.getPagination)

const itemsPerPageOptions = ref([10, 25, 50, 100])

const paginationInfo = computed(() => {
	const count = pagination.value?.count || 0
	const from = count === 0 ? 0 : (tablePage.value - 1) * tableItemsPerPage.value + 1
	const to = Math.min(tablePage.value * tableItemsPerPage.value, count)
	return { count, from, to }
})

const totalPages = computed(() => pagination.value?.pages || 1)

async function loadData() {
    await store.getAnomalyDetectorPages(tablePage.value, tableItemsPerPage.value)
}

function handleItemsPerPageChange(event: Event) {
	const target = event.target as HTMLSelectElement
	store.tableItemsPerPage = parseInt(target.value, 10)
	store.tablePage = 1
	loadData()
}

function loadPage(pageNumber: number) {
	if (pageNumber < 1 || pageNumber > totalPages.value) return
	store.tablePage = pageNumber
	loadData()
}

function getVisiblePages(currentPage: number, pages: number, maxVisible = 7) {
	const visiblePages: (number | string)[] = []

	if (pages <= maxVisible) {
		for (let i = 1; i <= pages; i++) {
			visiblePages.push(i)
		}
	} else {
		visiblePages.push(1)

		if (currentPage <= 4) {
			for (let i = 2; i <= 5; i++) {
				visiblePages.push(i)
			}
			visiblePages.push('...')
			visiblePages.push(pages)
		} else if (currentPage >= pages - 3) {
			visiblePages.push('...')
			for (let i = pages - 4; i <= pages; i++) {
				visiblePages.push(i)
			}
		} else {
			visiblePages.push('...')
			for (let i = currentPage - 1; i <= currentPage + 1; i++) {
				visiblePages.push(i)
			}
			visiblePages.push('...')
			visiblePages.push(pages)
		}
	}

	return visiblePages
}

function formatDecimal(value: number | string | undefined) {
    if (value === undefined || value === null) return '-'
    const num = typeof value === 'string' ? parseFloat(value) : value
	return num.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
}

function columnValue(row: any, key: string): string {
	switch (key) {
		case 'id':
			return String(row.id ?? '-')
		case 'company':
			return row.sensor?.companies?.[0]?.name ?? '-'
		case 'sensor_name':
			return row.sensor?.name ?? '-'
		case 'sensor_type':
			return row.sensor_type ?? '-'
		case 'data_field':
			return row.data_field ?? '-'
		case 'base_median':
			return formatDecimal(row.base_median)
		case 'base_iqr':
			return formatDecimal(row.base_iqr)
		case 'week_median':
			return formatDecimal(row.week_median)
		case 'week_iqr':
			return formatDecimal(row.week_iqr)
		case 'variation_level':
			return row.variation_level ?? '-'
		case 'week_alarms':
			return String(row.week_alarms ?? '-')
		case 'message':
			return row.message ?? '-'
		default:
			return '-'
	}
}

</script>

<template>
	<div class="bg-white py-2 shadow dark:bg-gray-900">
		<div v-if="!hasAppliedSearch" class="p-4 text-gray-500">
			{{ t('anomaly_detector.body.errors.apply_search_prompt') }}
		</div>

		<div v-else-if="rows.length <= 0" class="p-4 text-red-500">
			{{ t('anomaly_detector.body.errors.no_data_found') }}
		</div>

		<div v-else class="overflow-x-auto">
			<div class="mb-4 flex items-center justify-between px-4">
				<div class="text-sm text-gray-500">Total de registros: {{ paginationInfo.count }}</div>
				<div class="flex items-center space-x-2">
					<label for="itemsPerPage" class="text-sm font-medium text-gray-700 dark:text-gray-200"> Itens por página: </label>
					<div class="relative">
						<select
							id="itemsPerPage"
							v-model="tableItemsPerPage"
							@change="handleItemsPerPageChange"
							class="w-[65px] bg-white px-3 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white">
							<option
								v-for="option in itemsPerPageOptions"
								:key="option"
								:value="option"
								class="bg-white text-black dark:bg-gray-800 dark:text-white">
								{{ option.toString().padStart(2, '0') }}
							</option>
						</select>
						<div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 dark:text-gray-300">
							<svg class="h-2 w-5.5 text-blue-600" fill="currentColor" viewBox="0 0 10 5">
								<path d="M5 5L10 0H0l5 5z" />
							</svg>
						</div>
					</div>
				</div>
			</div>

			<table class="min-w-full table-auto text-left text-sm text-gray-700 dark:text-gray-200">
				<thead>
					<tr class="bg-gray-100 text-xs font-semibold uppercase text-gray-700 dark:bg-gray-800 dark:text-gray-200">
						<template v-for="(column, index) in columns" :key="index">
							<th
								scope="col"
								class="whitespace-nowrap px-4 py-3">
								<div class="flex items-center justify-between">
									<span>{{ column.title }}</span>
								</div>
							</th>
						</template>
					</tr>
				</thead>

				<tbody>
					<template v-for="(row, index) in rows" :key="row.id">
						<tr
							:class="[
								row.variation_level === 'critical'
									? 'bg-red-100 dark:bg-red-900/40'
									: index % 2 === 0
										? 'bg-white dark:bg-gray-800'
										: 'dark:bg-gray-850 bg-gray-50',
								'border-b border-gray-200 dark:border-gray-700',
							]">
								<td
									v-for="column in columns"
									:key="`${row.id}-${column.key}`"
									class="whitespace-nowrap px-4 py-3"
									:class="{ 'font-medium': column.key === 'id', capitalize: column.key === 'variation_level' }">
									<template v-if="column.key === 'variation'">
										<span
											class="inline-block px-2 py-1 text-xs font-semibold"
											:class="{
												'bg-green-100 text-green-800': row.variation_level === 'normal',
												'bg-yellow-100 text-yellow-800': row.variation_level === 'attention',
												'bg-red-100 text-red-800': row.variation_level === 'critical',
											}">
											{{ formatDecimal(row.variation) }}%
										</span>
									</template>
									<template v-else>
										{{ columnValue(row, column.key) }}
									</template>
								</td>
						</tr>
					</template>
				</tbody>
			</table>

			<div class="mt-4 flex flex-col items-center justify-between space-y-4 px-4 sm:flex-row sm:space-y-0">
				<div class="text-sm text-gray-500">Mostrando {{ paginationInfo.from }} a {{ paginationInfo.to }} de {{ paginationInfo.count }} registros</div>

				<div class="flex items-center space-x-2">
					<button
						:disabled="tablePage <= 1"
						@click="loadPage(tablePage - 1)"
						class="px-4 py-2 transition-colors duration-200"
						:class="{
							'cursor-not-allowed bg-gray-100 text-gray-400': tablePage <= 1,
							'bg-gray-100 text-gray-700 hover:bg-gray-200': tablePage > 1,
						}">
						<
					</button>

					<template v-for="n in getVisiblePages(tablePage, totalPages)" :key="n">
						<button
							v-if="typeof n === 'number'"
							@click="loadPage(n)"
							class="px-4 py-2 transition-colors duration-200"
							:class="{
								'bg-blue-600 text-white': n === tablePage,
								'bg-gray-100 text-gray-700 hover:bg-gray-200': n !== tablePage,
							}">
							{{ n }}
						</button>
						<span v-else class="px-2 py-2 text-gray-500">{{ n }}</span>
					</template>

					<button
						:disabled="tablePage >= totalPages"
						@click="loadPage(tablePage + 1)"
						class="px-4 py-2 transition-colors duration-200"
						:class="{
							'cursor-not-allowed bg-gray-100 text-gray-400': tablePage >= totalPages,
							'bg-gray-100 text-gray-700 hover:bg-gray-200': tablePage < totalPages,
						}">
						>
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
